import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChatCircleText, CheckCircle, Seal } from "@phosphor-icons/react";
import { Section, StatusBadge, STATUS_META } from "@/components/tax/shared";

export function Timeline({ items }) {
  return (
    <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4" data-testid="tax-timeline">
      {[...(items || [])].reverse().map((t) => (
        <li key={t.id} className="ml-4">
          <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full bg-red-600 ring-4 ring-white" />
          <p className="text-sm text-slate-800">{t.text}</p>
          <p className="text-xs text-slate-500 mt-0.5">{new Date(t.at).toLocaleString()} · {t.by}{t.status ? ` · ${STATUS_META[t.status]?.label}` : ""}</p>
        </li>
      ))}
    </ol>
  );
}

function Filing({ ret }) {
  if (!ret.filing) return null;
  return (
    <Section title="Filing confirmation" testId="tax-filing-confirmation">
      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <div><p className="text-xs text-slate-500">Filed on</p><p className="font-bold">{ret.filing.filed_date}</p></div>
        <div><p className="text-xs text-slate-500">EFILE confirmation #</p><p className="font-bold" data-testid="tax-filing-number">{ret.filing.confirmation_number}</p></div>
        <div><p className="text-xs text-slate-500">Filed by</p><p className="font-bold">{ret.preparer?.name} · {ret.filing.software}</p></div>
      </div>
      {ret.outcome && (
        <p className={`mt-4 text-sm font-semibold flex items-center gap-1.5 ${ret.outcome.outcome === "accepted" ? "text-emerald-700" : "text-red-600"}`} data-testid="tax-outcome">
          <Seal className="h-4 w-4" weight="fill" /> CRA {ret.outcome.outcome} on {ret.outcome.date}{ret.outcome.reason ? ` — ${ret.outcome.reason}` : ""}
        </p>
      )}
    </Section>
  );
}

export function StatusPanel({ ret, onRespond, responding, onWithdraw }) {
  const [msg, setMsg] = useState("");
  const open = (ret.requests || []).filter((r) => !r.resolved);
  return (
    <div className="space-y-5" data-testid="tax-status-step">
      <Section title="Case status" right={<StatusBadge status={ret.status} testId="tax-status-badge" />}>
        {ret.preparer ? <p className="text-sm text-slate-700">Shared with <strong>{ret.preparer.name}</strong>{ret.preparer.firm ? ` (${ret.preparer.firm})` : ""} on {new Date(ret.sharing_consent?.signed_at).toLocaleDateString()}.</p>
          : <p className="text-sm text-slate-500">Not yet shared with a preparer.</p>}
        {ret.t183?.final && !ret.t183?.superseded && <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-testid="tax-t183-signed"><CheckCircle className="h-4 w-4" weight="fill" /> T183 signed {new Date(ret.t183.signed_at).toLocaleString()}</p>}
        {ret.preparer && !["filed", "accepted"].includes(ret.status) && (
          <Button variant="ghost" size="sm" className="mt-3 text-red-600" data-testid="tax-withdraw-consent-btn"
            onClick={() => window.confirm("Withdraw consent? Your preparer will lose access immediately.") && onWithdraw()}>Withdraw consent</Button>
        )}
      </Section>
      {open.length > 0 && (
        <Section title="Your preparer needs information" testId="tax-open-requests">
          {open.map((q) => (
            <div key={q.id} className="flex gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900 mb-2">
              <ChatCircleText className="h-5 w-5 shrink-0" /><p>{q.message}<span className="block text-xs text-red-700/70 mt-1">{q.by} · {new Date(q.at).toLocaleString()}</span></p>
            </div>
          ))}
          <p className="text-xs text-slate-500 mb-2">You can update your answers or upload documents in the other tabs, then reply here to resubmit.</p>
          <textarea data-testid="tax-respond-text" rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full rounded-md border border-input p-3 text-sm" placeholder="Reply to your preparer…" />
          <div className="flex justify-end mt-2">
            <Button data-testid="tax-respond-btn" disabled={!msg.trim() || responding} onClick={() => onRespond(msg)} className="bg-red-600 hover:bg-red-700 text-white rounded-full">Reply & resubmit</Button>
          </div>
        </Section>
      )}
      <Filing ret={ret} />
      <Section title="Activity"><Timeline items={ret.timeline} /></Section>
    </div>
  );
}
